import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useFrameOrders } from '../hooks/useFrameOrders';
import { FrameOrderStats } from '../components/frame-orders/FrameOrderStats';
import { FrameOrderFilters } from '../components/frame-orders/FrameOrderFilters';
import { FrameOrderTable } from '../components/frame-orders/FrameOrderTable';
import { FollowUpButton } from '../components/frame-orders/FollowUpButton';
import { FollowUpModal } from '../components/frame-orders/FollowUpModal';
import { FrameOrderExclusionModal } from '../components/frame-orders/FrameOrderExclusionModal';
import { frameOrderApi } from '../services/frameOrderApi';

export function FrameOrderTrackingPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/frame-order-tracking');
    const {
        orders, total, stats, loading, error,
        filters, filterOptions,
        sortBy, sortOrder, page, limit,
        setFilters, setSort, setPage, refresh,
    } = useFrameOrders();

    // Load active employees for assignee selection
    const [assigneeOptions, setAssigneeOptions] = useState<string[]>([]);
    useEffect(() => {
        frameOrderApi.getActiveEmployees()
            .then(setAssigneeOptions)
            .catch(() => setAssigneeOptions([]));
    }, []);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
    const [followUpCount, setFollowUpCount] = useState(0);
    const [followUpLoading, setFollowUpLoading] = useState(false);
    const [followUpRefresh, setFollowUpRefresh] = useState(0);

    // Fetch total follow-up count for current filters (excluding documentId)
    useEffect(() => {
        setFollowUpLoading(true);
        const { documentId, ...filtersWithoutDocId } = filters;
        frameOrderApi.getFollowUps(filtersWithoutDocId)
            .then(data => setFollowUpCount(data.length))
            .catch(() => setFollowUpCount(0))
            .finally(() => setFollowUpLoading(false));
    }, [filters.companyCode, filters.documentType, filters.classificationCode, filters.salesTeam, filters.salesCoordinator, filters.salesManager, filters.search, followUpRefresh]);

    const handleClassificationClick = useCallback((label: string) => {
        const currentLabel = filters.classificationLabel;
        if (currentLabel === label) {
            // Toggle off: remove the classification filter
            const { classificationLabel, ...rest } = filters;
            setFilters(rest);
        } else {
            // Remove classificationCode when filtering by label to avoid conflicts
            const { classificationCode, ...rest } = filters;
            setFilters({ ...rest, classificationLabel: label });
        }
    }, [filters, setFilters]);

    const handleNavigateToRow = useCallback((documentId: string) => {
        setFilters({ ...filters, documentId });
    }, [filters, setFilters]);

    const handleCommentAdded = useCallback(() => {
        refresh();
        setFollowUpRefresh(c => c + 1);
    }, [refresh]);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Frame Order Tracking</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track open frame orders and consignment orders across companies
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {canEdit && (
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Edit Access
                        </span>
                    )}
                    <button
                        onClick={() => setSettingsOpen(true)}
                        title="Manage excluded customers"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <Settings className="h-4 w-4" /> Settings
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
                <FrameOrderStats
                    stats={stats}
                    total={total}
                    activeClassification={filters.classificationLabel}
                    onClassificationClick={handleClassificationClick}
                />
                <FollowUpButton
                    count={followUpCount}
                    loading={followUpLoading}
                    onClick={() => setFollowUpModalOpen(true)}
                />
            </div>

            {/* Filters */}
            <FrameOrderFilters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={setFilters}
            />

            {/* Error */}
            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Table */}
            <div className="relative">
                {loading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    </div>
                )}
                <FrameOrderTable
                    orders={orders}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={setSort}
                    canEdit={canEdit}
                    onCommentAdded={handleCommentAdded}
                    assigneeOptions={assigneeOptions}
                />
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">
                        Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" /> Prev
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Exclusion Settings Modal */}
            <FrameOrderExclusionModal
                isOpen={settingsOpen}
                onClose={() => { setSettingsOpen(false); refresh(); }}
                companies={filterOptions?.companies ?? []}
                canEdit={canEdit}
            />

            {/* Follow-up Modal */}
            <FollowUpModal
                isOpen={followUpModalOpen}
                onClose={() => setFollowUpModalOpen(false)}
                filters={filters}
                onNavigateToRow={handleNavigateToRow}
            />
        </div>
    );
}
